import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import * as Clipboard from "expo-clipboard";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View } from "react-native";

import {
  buildInviteUrl,
  listInvites,
  revokeInvite,
  type InviteKind,
  type InviteRow,
  type InviteStatus,
} from "../../../lib/invites";
import { supabase } from "../../../lib/supabase";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import {
  AdminButton,
  AdminCheckbox,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
} from "../../../components/admin/ui";
import { StatusPill, type StatusPillTone } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;
const TZ = "America/New_York";

const KINDS: (InviteKind | "all")[] = ["all", "client", "judge", "employee"];
const STATUSES: (InviteStatus | "all")[] = ["all", "pending", "accepted", "revoked", "expired"];

const STATUS_TONE: Record<InviteStatus, StatusPillTone> = {
  pending: "amber",
  accepted: "sage",
  revoked: "grey",
  expired: "grey",
};

function kindLabel(k: InviteKind): string {
  return k.charAt(0).toUpperCase() + k.slice(1);
}

function fmtAbsolute(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: TZ, timeZoneName: "short" });
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = t - Date.now();
  const sec = Math.round(diff / 1000);
  const futureFmt = (n: number, unit: string) => `expires in ${n} ${unit}${n === 1 ? "" : "s"}`;
  const pastFmt = (n: number, unit: string) => `expired ${n} ${unit}${n === 1 ? "" : "s"} ago`;
  const abs = Math.abs(sec);
  if (abs < 60) return diff > 0 ? "expires in <1m" : "just expired";
  const min = Math.round(abs / 60);
  if (min < 60) return diff > 0 ? futureFmt(min, "min") : pastFmt(min, "min");
  const hr = Math.round(min / 60);
  if (hr < 24) return diff > 0 ? futureFmt(hr, "hour") : pastFmt(hr, "hour");
  const day = Math.round(hr / 24);
  return diff > 0 ? futureFmt(day, "day") : pastFmt(day, "day");
}

export default function AdminInvitesScreen() {
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [competitionNames, setCompetitionNames] = useState<Record<string, string>>({});
  const [armedRevoke, setArmedRevoke] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedRef = useRef(false);
  const lastFilterKeyRef = useRef("");

  const filter = useAdminListFilter({
    keys: ["q", "kind", "status"],
    defaults: { q: "", kind: "all", status: "all" },
  });
  const search = filter.get("q");
  const kindFilter = filter.get("kind") as InviteKind | "all";
  const statusFilter = filter.get("status") as InviteStatus | "all";

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filter.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [armedBulkRevoke, setArmedBulkRevoke] = useState(false);
  const [armedRevokeAllExpired, setArmedRevokeAllExpired] = useState(false);
  const bulkArmedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiredArmedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    const filterKey = `${kindFilter}:${statusFilter}`;
    if (lastFilterKeyRef.current !== filterKey) {
      hasLoadedRef.current = false;
      lastFilterKeyRef.current = filterKey;
    }
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const list = await listInvites({
        kind: kindFilter === "all" ? undefined : kindFilter,
        status: statusFilter === "all" ? undefined : statusFilter,
      });
      setRows(list);
      const companyIds = [...new Set(list.map((r) => r.company_id).filter(Boolean))] as string[];
      const competitionIds = [...new Set(list.map((r) => r.competition_id).filter(Boolean))] as string[];
      const [compRes, compRes2] = await Promise.all([
        companyIds.length > 0 ? supabase.from("companies").select("id, name").in("id", companyIds) : Promise.resolve({ data: [] as { id: string; name: string }[] | null }),
        competitionIds.length > 0 ? supabase.from("competitions").select("id, name").in("id", competitionIds) : Promise.resolve({ data: [] as { id: string; name: string }[] | null }),
      ]);
      const cn: Record<string, string> = {};
      for (const c of (compRes.data ?? []) as { id: string; name: string }[]) cn[c.id] = c.name;
      const qn: Record<string, string> = {};
      for (const c of (compRes2.data ?? []) as { id: string; name: string }[]) qn[c.id] = c.name;
      setCompanyNames(cn);
      setCompetitionNames(qn);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load invites");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [kindFilter, statusFilter]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  useEffect(() => {
    setSelectedIds(new Set());
  }, [kindFilter, statusFilter, search]);

  useEffect(
    () => () => {
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      if (bulkArmedTimerRef.current) clearTimeout(bulkArmedTimerRef.current);
      if (expiredArmedTimerRef.current) clearTimeout(expiredArmedTimerRef.current);
    },
    []
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(q));
  }, [rows, search]);

  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((r) => selectedIds.has(r.id));
  function toggleAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const r of filteredRows) next.delete(r.id);
      else for (const r of filteredRows) next.add(r.id);
      return next;
    });
  }
  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onRevoke(id: string) {
    if (armedRevoke !== id) {
      setArmedRevoke(id);
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      armedTimerRef.current = setTimeout(() => setArmedRevoke(null), 4000);
      return;
    }
    if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    setArmedRevoke(null);
    setErr(null);
    try { await revokeInvite(id); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Revoke failed"); }
  }

  function onCopy(id: string, token: string) {
    void Clipboard.setStringAsync(buildInviteUrl(token));
    setCopiedId(id);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopiedId(null), 1600);
  }

  async function onBulkRevoke() {
    if (!armedBulkRevoke) {
      setArmedBulkRevoke(true);
      if (bulkArmedTimerRef.current) clearTimeout(bulkArmedTimerRef.current);
      bulkArmedTimerRef.current = setTimeout(() => setArmedBulkRevoke(false), 4000);
      return;
    }
    setArmedBulkRevoke(false);
    if (bulkArmedTimerRef.current) clearTimeout(bulkArmedTimerRef.current);
    setErr(null);
    try {
      const targets = filteredRows.filter((r) => selectedIds.has(r.id) && r.status === "pending");
      await Promise.all(targets.map((r) => revokeInvite(r.id)));
      setSelectedIds(new Set());
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : "Bulk revoke failed"); }
  }

  function onBulkCopy() {
    const links = filteredRows.filter((r) => selectedIds.has(r.id)).map((r) => `${r.email} → ${buildInviteUrl(r.token)}`).join("\n");
    if (links) void Clipboard.setStringAsync(links);
  }

  function onBulkResend() {
    const links = filteredRows.filter((r) => selectedIds.has(r.id) && r.status === "pending").map((r) => `${r.email} → ${buildInviteUrl(r.token)}`).join("\n");
    if (links) void Clipboard.setStringAsync(links);
  }

  async function onRevokeAllExpired() {
    if (!armedRevokeAllExpired) {
      setArmedRevokeAllExpired(true);
      if (expiredArmedTimerRef.current) clearTimeout(expiredArmedTimerRef.current);
      expiredArmedTimerRef.current = setTimeout(() => setArmedRevokeAllExpired(false), 4000);
      return;
    }
    setArmedRevokeAllExpired(false);
    if (expiredArmedTimerRef.current) clearTimeout(expiredArmedTimerRef.current);
    setErr(null);
    try {
      const targets = filteredRows.filter((r) => r.status === "expired" || (r.status === "pending" && new Date(r.expires_at).getTime() < Date.now()));
      await Promise.all(targets.map((r) => revokeInvite(r.id)));
      setSelectedIds(new Set());
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : "Bulk revoke failed"); }
  }

  const selectedCount = selectedIds.size;
  const empty = !loading && filteredRows.length === 0;

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Inbox · Invites"
          title="Invites"
          subtitle="Magic links for clients, judges, and admin team members."
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        {/* Filter bar */}
        <View style={{ padding: 12, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, gap: 10 }}>
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search by email…" autoCapitalize="none" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Kind:</Text>
            {KINDS.map((k) => (
              <FilterChip key={k} theme={THEME} label={k === "all" ? "All" : kindLabel(k)} active={kindFilter === k} onPress={() => filter.set({ kind: k })} />
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Status:</Text>
            {STATUSES.map((s) => (
              <FilterChip key={s} theme={THEME} label={s === "all" ? "All" : s} active={statusFilter === s} onPress={() => filter.set({ status: s })} />
            ))}
            {statusFilter === "expired" && filteredRows.length > 0 ? (
              <AdminButton theme={THEME} label={armedRevokeAllExpired ? "Click again to revoke all" : "Revoke all expired"} variant="destructive" size="sm" onPress={() => void onRevokeAllExpired()} />
            ) : null}
          </View>
        </View>

        {/* Bulk action bar */}
        {selectedCount > 0 ? (
          <View style={{ marginTop: 10, padding: 12, borderRadius: RADII.md, backgroundColor: THEME.accentSoft, borderWidth: STROKES.hairline, borderColor: THEME.accentBorderHover, flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 11, letterSpacing: 1.2, color: THEME.accent }}>{selectedCount} selected</Text>
            <Pressable onPress={() => setSelectedIds(new Set())} style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}>
              <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Clear</Text>
            </Pressable>
            <View style={{ flex: 1 }} />
            <AdminButton theme={THEME} label={armedBulkRevoke ? `Click again to revoke ${selectedCount}` : "Revoke selected"} variant="destructive" size="sm" onPress={() => void onBulkRevoke()} />
            <AdminButton theme={THEME} label="Resend (copy)" variant="outline" size="sm" onPress={onBulkResend} />
            <AdminButton theme={THEME} label="Copy all links" variant="outline" size="sm" onPress={onBulkCopy} />
          </View>
        ) : null}

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading invites…" />
          ) : empty ? (
            <AdminEmpty theme={THEME} title="No invites match these filters." />
          ) : (
            <View style={{ gap: 8 }}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 12, paddingHorizontal: 14, paddingVertical: 6 }}>
                <AdminCheckbox theme={THEME} checked={allVisibleSelected} onPress={toggleAllVisible} />
                <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
                  {allVisibleSelected ? "Deselect all" : `Select all visible (${filteredRows.length})`}
                </Text>
              </View>
              {filteredRows.map((r) => {
                const armed = armedRevoke === r.id;
                const sel = selectedIds.has(r.id);
                return (
                  <View key={r.id} style={{ padding: 14, borderRadius: RADII.md, backgroundColor: sel ? THEME.accentSoft : THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: sel ? THEME.accentBorderHover : THEME.border }}>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                      <AdminCheckbox theme={THEME} checked={sel} onPress={() => toggleOne(r.id)} />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                          <StatusPill theme={THEME} label={kindLabel(r.kind)} tone="amber" showDot={false} />
                          <StatusPill theme={THEME} label={r.status} tone={STATUS_TONE[r.status]} />
                          <View style={{ flex: 1 }} />
                          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>{fmtRelative(r.expires_at)}</Text>
                        </View>
                        <Text style={{ marginTop: 6, fontFamily: THEME.bodyMediumFont, fontSize: 14, color: THEME.textOnSurface }}>{r.email}</Text>
                        {r.company_id || r.competition_id ? (
                          <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
                            {r.company_id ? `Company: ${companyNames[r.company_id] ?? r.company_id}` : null}
                            {r.company_id && r.competition_id ? " · " : ""}
                            {r.competition_id ? `Competition: ${competitionNames[r.competition_id] ?? r.competition_id}` : null}
                          </Text>
                        ) : null}
                        {r.role ? <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Role: {r.role}</Text> : null}
                        <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 10.5, color: THEME.textOnSurfaceFaint }}>{fmtAbsolute(r.expires_at)}</Text>
                      </View>
                    </View>
                    <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                      <AdminButton theme={THEME} label={copiedId === r.id ? "Copied" : "Copy link"} variant="outline" size="sm" onPress={() => onCopy(r.id, r.token)} />
                      {r.status === "pending" ? (
                        <AdminButton theme={THEME} label={armed ? "Click again" : "Revoke"} variant="destructive" size="sm" onPress={() => void onRevoke(r.id)} />
                      ) : null}
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
